/**
 * Settings › Connections › Work blocks: is the Google side connected, what
 * did the last run do, and is automatic planning on. The answers to "is it
 * working?" without opening Cloudflare or Apps Script.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import { useRunBlocks, useWorkBlockConfig, useWorkBlockSettings } from "@/hooks/useWorkBlocks";
import { appMode } from "@/lib/pocketbase";
import { toIsoLocal } from "@/lib/timeRollup";
import { DAY_LABEL, formatTime, hmm, nextMonday, parseTime, weekLabel } from "@/lib/workBlocks";

const statusText: React.CSSProperties = { fontSize: 12.5, maxWidth: 360, textAlign: "right" };

/** Connected or not, and a test that reads your calendars without writing. */
export function WorkBlockConnection() {
  const { data: config, isLoading } = useWorkBlockConfig();
  const run = useRunBlocks();
  const [tested, setTested] = useState<string | null>(null);

  if (appMode !== "live") return <span style={{ ...statusText, color: "var(--muted)" }}>Demo: nothing is sent to Google.</span>;
  if (isLoading || !config) return <span style={{ ...statusText, color: "var(--muted)" }}>Checking…</span>;
  if (config.error) return <span style={{ ...statusText, color: "var(--bad)" }}>{config.error}</span>;

  const missing = [!config.token && "BLOCKS_TOKEN", !config.scriptUrl && "BLOCKS_SCRIPT_URL"].filter(Boolean);
  if (missing.length) {
    return (
      <span style={{ ...statusText, color: "var(--warn)" }}>
        Not connected: the Worker is missing {missing.join(" and ")}. Steps in docs/work-blocks/README.md.
      </span>
    );
  }

  async function test() {
    setTested(null);
    const week = nextMonday(toIsoLocal(new Date()));
    try {
      const r = await run.mutateAsync({ weekStart: week, dryRun: true });
      setTested(`ok:Connected. Read your calendars: ${hmm(r.freeMinutes) || "0:00"} free ${weekLabel(week)}.`);
    } catch (err) {
      setTested(`err:${err instanceof Error ? err.message : String(err)}`);
    }
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
      {tested?.startsWith("ok:") && <span style={{ ...statusText, color: "var(--good)" }}>{tested.slice(3)}</span>}
      {tested?.startsWith("err:") && <span style={{ ...statusText, color: "var(--bad)" }}>{tested.slice(4)}</span>}
      {!tested && <span style={{ ...statusText, color: "var(--muted)" }}>Settings in place</span>}
      <button type="button" onClick={() => void test()} disabled={run.isPending} style={button}>
        {run.isPending ? "Checking…" : "Test connection"}
      </button>
    </div>
  );
}

/** What the last run did, and when. */
export function WorkBlockLastRun() {
  const { data } = useWorkBlockSettings();
  const last = data?.lastRun;
  if (appMode !== "live") return <span style={{ ...statusText, color: "var(--muted)" }}>—</span>;
  if (!last) return <span style={{ ...statusText, color: "var(--muted)" }}>No run yet</span>;

  const at = new Date(last.at);
  const stale = Date.now() - at.getTime() > 8 * 86_400_000;
  const when = at.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  const parts = [
    `${when}, ${last.source === "auto" ? "automatically" : "from Send to Google"}`,
    `week of ${weekLabel(last.weekStart)}`,
    `${last.placed} placed`,
    ...(last.missed ? [`${last.missed} found no slot`] : []),
    ...(last.errors.length ? [last.errors[0]] : []),
  ];
  return (
    <span style={{ ...statusText, color: last.errors.length ? "var(--bad)" : stale || last.missed ? "var(--warn)" : "var(--ink2)" }}>
      {parts.join(" · ")}
      {stale && " · nothing for over a week"}
    </span>
  );
}

/** On or off, and when. Changed on the Planner, where the blocks are. */
export function WorkBlockAuto() {
  const { data } = useWorkBlockSettings();
  const s = data?.settings;
  if (!s) return null;
  return (
    <span style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
      <span style={{ ...statusText, color: "var(--ink2)" }}>
        {s.autoRun
          ? `On: ${DAY_LABEL[s.autoRunDay]} at ${formatTime(parseTime(s.autoRunTime) ?? 17 * 60)}`
          : "Off: only Send to Google"}
      </span>
      <Link to="/planner" style={{ fontSize: 12.5, color: "var(--accent)" }}>Change in Planner</Link>
    </span>
  );
}

const button: React.CSSProperties = {
  padding: "7px 14px", borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", fontSize: 12.5, color: "var(--ink2)", cursor: "pointer",
};
